import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';

/**
 * Separate store for undo/redo history
 * This prevents unnecessary re-renders in the main flowchart store
 */
const useHistoryStore = create((set, get) => ({
  past: [],
  future: [],
  
  addToHistory: (state) => {
    set((store) => ({
      past: [...store.past, state],
      future: [], // Clear future when new action is taken
    }));
  },
  
  undo: () => {
    const { past } = get();
    if (past.length === 0) return null;
    
    const previous = past[past.length - 1];
    set({
      past: past.slice(0, -1),
      future: [previous, ...get().future],
    });
    
    return previous;
  },
  
  redo: () => {
    const { future } = get();
    if (future.length === 0) return null;
    
    const next = future[0];
    set({
      past: [...get().past, next],
      future: future.slice(1),
    });
    
    return next;
  },
  
  canUndo: () => get().past.length > 0,
  canRedo: () => get().future.length > 0,
  
  clearHistory: () => set({ past: [], future: [] }),
  
  setMaxHistorySize: (size) => {
    set((store) => ({
      past: store.past.slice(-size),
    }));
  },
}));

/**
 * Enhanced Undo/Redo Hook
 * 
 * Features:
 * - Keyboard shortcuts (Ctrl+Z, Ctrl+Y)
 * - Max history size limit
 * - Debounced snapshots
 * - Better performance
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.maxHistorySize - Maximum history entries (default: 50)
 * @param {boolean} options.enableShortcuts - Enable keyboard shortcuts (default: true)
 * @param {number} options.debounceMs - Debounce time for snapshots (default: 300)
 */
const useUndoRedo = ({
  maxHistorySize = 50,
  enableShortcuts = true,
  debounceMs = 300,
} = {}) => {
  const { 
    past, 
    future, 
    addToHistory, 
    undo: undoHistory, 
    redo: redoHistory,
    canUndo: checkCanUndo,
    canRedo: checkCanRedo,
    clearHistory,
    setMaxHistorySize,
  } = useHistoryStore();

  const debounceTimerRef = useRef(null);
  const currentStateRef = useRef(null);

  // Update max history size
  useEffect(() => {
    setMaxHistorySize(maxHistorySize);
  }, [maxHistorySize, setMaxHistorySize]);

  /**
   * Take a snapshot of current state
   * Debounced to prevent too many history entries
   */
  const takeSnapshot = useCallback((state) => {
    // Store current state
    currentStateRef.current = state;

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (currentStateRef.current) {
        addToHistory({
          nodes: JSON.parse(JSON.stringify(currentStateRef.current.nodes)),
          edges: JSON.parse(JSON.stringify(currentStateRef.current.edges)),
          timestamp: Date.now(),
        });
      }
    }, debounceMs);
  }, [addToHistory, debounceMs]);

  /**
   * Undo last action
   */
  const undo = useCallback((currentState, applyState) => {
    const previousState = undoHistory();
    if (previousState && applyState) {
      applyState(previousState);
    }
    return previousState;
  }, [undoHistory]);

  /**
   * Redo last undone action
   */
  const redo = useCallback((currentState, applyState) => {
    const nextState = redoHistory();
    if (nextState && applyState) {
      applyState(nextState);
    }
    return nextState;
  }, [redoHistory]);

  /**
   * Check if can undo
   */
  const canUndo = useCallback(() => {
    return checkCanUndo();
  }, [checkCanUndo]);

  /**
   * Check if can redo
   */
  const canRedo = useCallback(() => {
    return checkCanRedo();
  }, [checkCanRedo]);

  /**
   * Clear all history
   */
  const clear = useCallback(() => {
    clearHistory();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
  }, [clearHistory]);

  /**
   * Keyboard shortcuts
   */
  useEffect(() => {
    if (!enableShortcuts) return;

    const handleKeyDown = (event) => {
      // Ctrl+Z or Cmd+Z - Undo
      if ((event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey) {
        event.preventDefault();
        undo();
      }
      
      // Ctrl+Shift+Z or Cmd+Shift+Z or Ctrl+Y - Redo
      if (
        ((event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey) ||
        ((event.ctrlKey || event.metaKey) && event.key === 'y')
      ) {
        event.preventDefault();
        redo();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [enableShortcuts, undo, redo]);

  return {
    takeSnapshot,
    undo,
    redo,
    canUndo: canUndo(),
    canRedo: canRedo(),
    clear,
    historySize: past.length,
    futureSize: future.length,
  };
};

export default useUndoRedo;